import {useCallback, useEffect} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getCountriesQuery from '../graphql/getCountriesQuery';
import {
  countriesFetchFailed,
  countriesFetchStarted,
  countriesFetchSucceeded
} from '../store/slice';
import {Country} from '../store/types';

interface UseCountriesResult {
  countries: Country[] | null;
  refetchCountries: () => Promise<void>;
}

export const useCountries = (): UseCountriesResult => {
  const countries = useAppSelector(({countries}) => countries.list);
  const countriesLoading = useAppSelector(
    ({countries}) => countries.countriesLoading
  );
  const dispatch = useAppDispatch();

  const [fetchCountries] = useLazyQuery<{countries: Country[]}>(
    getCountriesQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  );

  const handleError = useCallback(
    (errorMessage: string) => {
      console.error(errorMessage);
      dispatch(countriesFetchFailed());
    },
    [dispatch]
  );

  const getCountries = useCallback(
    async (force = false) => {
      // Only fetch if countries are missing AND not already loading, unless forced
      if (!force && (Array.isArray(countries) || countriesLoading)) return;

      try {
        dispatch(countriesFetchStarted());
        const {data, error} = await fetchCountries();

        if (error) {
          const errorMessage = error.message ?? 'Failed to fetch countries.';
          handleError(errorMessage);
          return;
        }

        const fetchedCountries = data?.countries ?? [];
        dispatch(countriesFetchSucceeded(fetchedCountries));
      } catch (err) {
        handleError(`Unexpected error while fetching countries: ${err}`);
      }
    },
    [countries, countriesLoading, dispatch, fetchCountries, handleError]
  );

  useEffect(() => {
    getCountries();
  }, []); // eslint-disable-line

  const refetchCountries = useCallback(
    () => getCountries(true),
    [getCountries]
  );

  return {countries, refetchCountries};
};
